import React, { useEffect, useState } from "react";
import Dashboard from "../../layouts/Dashboard";
import { Card, Steps, Tooltip, Collapse, Modal, Switch, message } from "antd";
import LoadingArea from "../../components/LoadingArea";
import axios from "axios";
import BASE from "../../config";
import ReactLoading from "react-loading";
import { SearchOutlined, EditOutlined, DownloadOutlined } from "@ant-design/icons";
import { Input } from "antd";
import { Table } from "antd";
import { DatePicker, Tag } from "antd";
import moment from "moment";
import { formatDateDDMMYYYY } from "../../utils/commonFunctions";
import useAxios from "../../hooks/useAxios";
import useRole from "../../hooks/useRole";
import { checkPermission } from "../../utils/checkPermission";
import exportFromJSON from "export-from-json";

const { RangePicker } = DatePicker;

const AmeyoService = () => {
  const api = useAxios();
  const [open, setOpen] = useState(false);
  const [disableEdit, setDisableEdit] = useState(true);
  const [load, setLoad] = useState(false);
  const [disable, setDisable] = useState(true);
  const [editableData, setEditableData] = useState({
    _id: "",
    name: "",
    start: "",
    end: "",
    active: "",
  });
  const oneMonthLater = new Date();
  oneMonthLater.setMonth(oneMonthLater.getMonth() + 2);
  const [active, setActive] = useState(null);
  const [serviceName, setServiceName] = useState("");
  const [tableData, setTableData] = useState([]);
  const [defaultDate, setDefaultDate] = useState([
    moment(new Date()).format("YYYY-MM-DD"),
    moment(new Date(oneMonthLater)).format("YYYY-MM-DD"),
  ]);
  const [canViewAmeyoService, setCanViewAmeyoService] = useState(false);
  const [canToggleAmeyoService, setCanToggleAmeyoService] = useState(false);
  const userRole = useRole();

  const checkPermissions = async () => {
    try {
      const [viewAllowed, toggleAllowed] = await Promise.all([
        checkPermission(userRole, "ameyo_service_module", "view_ameyo_service"),
        checkPermission(userRole, "ameyo_service_module", "toggle_ameyo_service"),
      ]);
      setCanViewAmeyoService(viewAllowed);
      setCanToggleAmeyoService(toggleAllowed);
    } catch (error) {
      console.error("Permission check failed for Ameyo Service Module:", error);
    }
  };

  const columns = [
    {
      title: "Service Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Actions",
      dataIndex: "action",
      key: "action",
      fixed: "right",
      align: "center",
      render: (text, record) => {
        return (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ gap: "1rem" }}
          >
            <Switch
              onChange={editHandling}
              checkedChildren="ON"
              unCheckedChildren="OFF"
              checked={record?.active}
              disabled={!canToggleAmeyoService}
            />
          </div>
        );
      },
    },
  ];

  const handleDate = (value, dateString) => {
    if (value === null) {
      setDefaultDate([
        moment(new Date()).format("YYYY-MM-DD"),
        moment(new Date()).format("YYYY-MM-DD"),
      ]);
    } else {
      setDefaultDate(dateString);
    }
  };

  const editHandleDate = (value, dateString) => {
    if (value === null) {
      setDisableEdit({
        ...editableData,
        start: moment(new Date()).format("YYYY-MM-DD"),
        end: moment(new Date()).format("YYYY-MM-DD"),
      });
    } else {
      setEditableData({
        ...editableData,
        start: dateString[0],
        end: dateString[1],
      });
    }
  };

  const [logsData, setLogsData] = useState([]);
  const [logsLoad, setLogsLoad] = useState(false);
  const [logDateRange, setLogDateRange] = useState([null, null]);

  const logColumns = [
    {
      title: "S No.",
      dataIndex: "sno",
      key: "sno",
      width: 70,
      align: "center",
    },
    {
      title: "Changed By",
      dataIndex: "user",
      key: "user",
    },
    {
      title: "Toggle Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      width: 120,
    },
    {
      title: "Message",
      dataIndex: "message",
      key: "message",
    },
    {
      title: "Date & Time",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 220,
    },
    {
      title: "IP Address",
      dataIndex: "ipAddress",
      key: "ipAddress",
      width: 150,
    },
  ];

  const fetchAmeyoLogs = async (startDate, endDate) => {
    setLogsLoad(true);
    try {
      let query = "";
      if (startDate && endDate) {
        query = `?startDate=${startDate}&endDate=${endDate}`;
      }
      const res = await api.get(`/AmeyoServiceSettings/logs${query}`, {
        tokenType: "user",
      });
      const rawLogs = res.data?.logs || [];
      const formatted = rawLogs.map((item, index) => {
        const isSwitchedOn = item.metadata?.toggleStatus === "ON" || (item.message && item.message.includes("ON"));
        return {
          key: item._id || index,
          sno: index + 1,
          user: item.userId?.name
            ? `${item.userId.name} (${item.userId.email || item.userId.role || ""})`
            : "System / Unknown",
          status: isSwitchedOn ? (
            <Tag color="success">ON</Tag>
          ) : (
            <Tag color="error">OFF</Tag>
          ),
          message: item.message,
          timestamp: item.timestamp
            ? moment(item.timestamp).format("DD-MM-YYYY, hh:mm:ss A")
            : "-",
          ipAddress: item.ipAddress || "-",
        };
      });
      setLogsData(formatted);
    } catch (err) {
      console.error("Failed to fetch Ameyo logs:", err);
    } finally {
      setLogsLoad(false);
    }
  };

  const handleLogDateChange = (dates, dateStrings) => {
    setLogDateRange(dates ? [dateStrings[0], dateStrings[1]] : [null, null]);
  };

  const handleFilterLogs = () => {
    if (logDateRange[0] && logDateRange[1]) {
      fetchAmeyoLogs(logDateRange[0], logDateRange[1]);
    } else {
      fetchAmeyoLogs();
    }
  };

  const handleResetLogs = () => {
    setLogDateRange([null, null]);
    fetchAmeyoLogs();
  };

  const downloadCSV = async () => {
    try {
      setLogsLoad(true);
      let query = "";
      if (logDateRange[0] && logDateRange[1]) {
        query = `?startDate=${logDateRange[0]}&endDate=${logDateRange[1]}`;
      }
      const res = await api.get(`/AmeyoServiceSettings/logs${query}`, {
        tokenType: "user",
      });
      const rawLogs = res.data?.logs || [];
      if (rawLogs.length === 0) {
        message.warning("No logs found to export.");
        return;
      }

      const exportData = rawLogs.map((item, index) => {
        const isSwitchedOn = item.metadata?.toggleStatus === "ON" || (item.message && item.message.includes("ON"));
        return {
          "S No.": index + 1,
          "User Name": item.userId?.name || "System / Unknown",
          "User Email": item.userId?.email || "-",
          "User Role": item.userId?.role || "-",
          "Toggle Status": isSwitchedOn ? "ON" : "OFF",
          "Activity Message": item.message || "-",
          "Date & Time": item.timestamp ? moment(item.timestamp).format("DD-MM-YYYY hh:mm:ss A") : "-",
          "IP Address": item.ipAddress || "-",
        };
      });

      const fileName = `Ameyo_Toggle_Logs_${moment().format("DD-MM-YYYY_HH-mm")}`;
      exportFromJSON({ data: exportData, fileName, exportType: "csv" });
      message.success("CSV downloaded successfully!");
    } catch (err) {
      console.error("Error exporting CSV:", err);
      message.error("Failed to download CSV.");
    } finally {
      setLogsLoad(false);
    }
  };

  const editHandling = async (checked) => {
    setLoad(true);
    try {
      await api.put(
        `/AmeyoServiceSettings/${editableData._id}`,
        {
          serviceName: editableData.name,
          startDate: editableData.start,
          endDate: editableData.end,
          isActive: checked,
        },
        {
          tokenType: "user",
        }
      );
      await getAllServices();
      await fetchAmeyoLogs();
      setOpen(false);
    } catch (err) {
      console.log(err);
    } finally {
      setLoad(false);
    }
  };

  const getAllServices = async () => {
    setLoad(true);
    try {
      let service = await api.get(`/AmeyoServiceSettings`, {
        tokenType: "user",
      });
      let ameyoSettings = service.data?.AmeyoServiceSettings;
      
      if (!ameyoSettings) {
        const seedResponse = await api.post(
          `/AmeyoServiceSettings`,
          {
            serviceName: "Ameyo Toggle",
            isActive: false,
            status: true,
          },
          {
            tokenType: "user",
          }
        );
        ameyoSettings = seedResponse.data?.data;
      }

      setEditableData({
        ...editableData,
        _id: ameyoSettings?._id,
        name: ameyoSettings?.serviceName || "Ameyo Toggle",
        start: ameyoSettings?.startDate,
        end: ameyoSettings?.endDate,
        active: ameyoSettings?.isActive,
      });

      let data = [];
      if (ameyoSettings) {
        data.push({
          sno: 1,
          name: ameyoSettings.serviceName,
          start_date: ameyoSettings.startDate ? formatDateDDMMYYYY(new Date(ameyoSettings.startDate)) : "-",
          end_date: ameyoSettings.endDate ? formatDateDDMMYYYY(new Date(ameyoSettings.endDate)) : "-",
          status: ameyoSettings.isActive ? (
            <Tag color="success">Active</Tag>
          ) : (
            <Tag color="error">In Active</Tag>
          ),
          _id: ameyoSettings._id,
          active: ameyoSettings.isActive,
        });
      }
      setTableData([...data]);
    } catch (err) {
      console.log(err);
    } finally {
      setLoad(false);
    }
  };

  useEffect(() => {
    getAllServices();
    fetchAmeyoLogs();
  }, []);

  useEffect(() => {
    if (userRole) {
      checkPermissions();
    }
  }, [userRole]);

  useEffect(() => {
    if (serviceName !== "" && active !== "") {
      setDisable(false);
    } else {
      setDisable(true);
    }
  }, [serviceName, active]);

  useEffect(() => {
    if (editableData.name !== "" && editableData.active !== "") {
      setDisableEdit(false);
    } else {
      setDisableEdit(true);
    }
  }, [editableData]);

  if (!canViewAmeyoService) {
    return <div></div>;
  }

  return (
    <Dashboard title="Digital Document">
      {(load || logsLoad) && (
        <ReactLoading type="spin" color="#0000FF" className="spinnerBg" />
      )}
      <div className="row">
        <div className="col-md-12 mb-4">
          <Card title="Ameyo Service" className="border mt-3">
            <div style={{ width: "100%" }}>
              <Table
                columns={columns}
                dataSource={tableData}
                pagination={false}
                scroll={{ x: true }}
                style={{ whiteSpace: "pre" }}
              />
            </div>
          </Card>
        </div>

        <div className="col-md-12 mb-4">
          <Card
            title="Ameyo Toggle Activity Logs"
            className="border"
            extra={
              <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                <RangePicker
                  format="YYYY-MM-DD"
                  value={
                    logDateRange[0] && logDateRange[1]
                      ? [moment(logDateRange[0]), moment(logDateRange[1])]
                      : null
                  }
                  onChange={handleLogDateChange}
                  placeholder={["Start Date", "End Date"]}
                  style={{ height: "34px" }}
                />
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={handleFilterLogs}
                  style={{ padding: "4px 12px", height: "34px" }}
                >
                  <SearchOutlined className="mr-1" /> Filter
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleResetLogs}
                  style={{ padding: "4px 12px", height: "34px" }}
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="btn btn-success btn-sm d-flex align-items-center"
                  onClick={downloadCSV}
                  disabled={logsLoad}
                  style={{ padding: "4px 12px", height: "34px", gap: "5px" }}
                >
                  <DownloadOutlined /> Download CSV
                </button>
              </div>
            }
          >
            <div style={{ width: "100%" }}>
              <Table
                columns={logColumns}
                dataSource={logsData}
                loading={logsLoad}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50", "100"],
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} logs`,
                }}
                scroll={{ x: true }}
                style={{ whiteSpace: "pre" }}
              />
            </div>
          </Card>
        </div>
      </div>
    </Dashboard>
  );
};

export default AmeyoService;
